import React, { useState, useEffect, useMemo } from "react";
import { Plus, Truck, X, Trash2, Mail, ChevronDown, ChevronUp, Package, MapPin, User, Phone, PenLine, AlertTriangle, Clock, Loader2, Printer, CheckSquare, Square, Navigation, Paperclip, Euro, Search, Edit, Download } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AddressBookPicker from "../../components/AddressBookPicker";
import { printShipmentLabel as printInternalLabel, printMultipleLabels as printMultipleInternalLabels } from "../../components/customer/ShipmentLabel";
import { authHeaders } from "@/hooks/useAdminData";
import { formatDateTimeIT } from "@/utils/datetime";
import { getCache, setCache } from "@/utils/dataCache";
import ExtraServicesEditor, { ExtraServicesBadges } from "@/components/ExtraServicesEditor";
import UrgencyCostInput from "@/components/UrgencyCostInput";
import { extrasCost, hasAnyExtra } from "@/utils/jobCost";
import { apiErrorMessage } from "@/utils/apiError";

const PAGE_SIZE = 50;

const downloadPdf = (file) => {
  const binary = atob(file.data_base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const blob = new Blob([bytes], { type: file.content_type || "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const downloadPodPdf = async (tracking) => {
  if (!tracking) return;
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/tracking/${tracking}/pod`;
    const res = await axios.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `POD_${tracking}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 30000);
    toast.success("POD scaricato");
  } catch {
    toast.error("Errore durante il download del POD");
  }
};

const urgencyLabel = (u) => {
  if (u === "urgente") return { label: "URGENTE", cls: "bg-red-100 text-red-700 border border-red-300" };
  if (u === "express") return { label: "EXPRESS", cls: "bg-orange-100 text-orange-700 border border-orange-300" };
  return { label: "NORMALE", cls: "bg-zinc-100 text-zinc-600 border border-zinc-200" };
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: "in_attesa", label: "Presa in Carico", style: "bg-zinc-100 text-zinc-700" },
  { value: "ritirato", label: "Ritirato", style: "bg-blue-100 text-blue-700" },
  { value: "in_transito", label: "In Transito", style: "bg-orange-100 text-orange-700" },
  { value: "in_consegna", label: "In Consegna", style: "bg-amber-100 text-amber-700" },
  { value: "consegnato", label: "Consegnato", style: "bg-green-100 text-green-700" },
  { value: "cancellation_requested", label: "Annullamento Richiesto", style: "bg-red-100 text-red-700" },
  { value: "annullato", label: "Annullato", style: "bg-red-200 text-red-800" },
];

const statusLabel = (s) => statusOptions.find((o) => o.value === s)?.label || s;
const statusStyle = (s) => statusOptions.find((o) => o.value === s)?.style || "bg-zinc-100 text-zinc-700";

const getPickupPointPreview = (details, fallback) => {
  const title = details?.name || details?.company || fallback || "-";
  const address = details?.address || "-";
  const locality = [details?.cap, details?.city || fallback, details?.province ? `(${details.province})` : ""]
    .filter(Boolean)
    .join(" ");

  return { title, address, locality: locality || fallback || "-" };
};

const getReadyBadgeLabel = (item) => {
  if (item.goods_ready_status !== "pronta") return null;
  return item.goods_ready_time ? `Pronta alle ${item.goods_ready_time}` : "Pronta";
};

const getDdtBadgeLabel = (item) => {
  const count = item.ddt_files?.length || 0;
  if (!count) return null;
  return count > 1 ? `DDT PDF (${count})` : "DDT PDF";
};

const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const emptyForm = {
  client_email: "", client_name: "", company: "",
  origin_name: "", origin_company: "", origin_address: "", origin_city: "", origin_cap: "", origin_province: "", origin_phone: "",
  dest_name: "", dest_company: "", dest_address: "", dest_city: "", dest_cap: "", dest_province: "", dest_phone: "", dest_email: "",
  colli: [{ tipo: "collo", peso: "", dimensioni: "", quantita: 1 }],
  service_type: "standard", urgency: "normale", urgency_cost: "", note: "", ddt_reference: "", scheduled_date: "", descrizione_merce: "",
  extra_services: {},
};

export default function AdminPickups() {
  const [pickups, setPickups] = useState([]);
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [driverLocations, setDriverLocations] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [goodsReadyMode, setGoodsReadyMode] = useState("none");
  const [goodsReadyTime, setGoodsReadyTime] = useState("");
  const [showStatus, setShowStatus] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", location: "", note: "" });
  const [expandedRow, setExpandedRow] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortDriverId, setSortDriverId] = useState("");
  const [filterMonth, setFilterMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [searchTracking, setSearchTracking] = useState("");
  const [insurance, setInsurance] = useState(false);
  const [insuranceValue, setInsuranceValue] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [loadedCount, setLoadedCount] = useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);

  const buildUrl = (limit) => {
    const params = new URLSearchParams();
    if (filterMonth) params.set("month", filterMonth);
    params.set("limit", String(limit));
    return `${API}/admin/pickups?${params.toString()}`;
  };

  const load = () => {
    const h = authHeaders();
    const url = buildUrl(loadedCount);
    // Serve cached data instantly while we refresh in the background.
    const cached = getCache(url);
    if (cached) {
      setPickups(cached.data);
      setTotalCount(cached.total);
    }
    axios.get(url, h).then((r) => {
      const total = parseInt(r.headers["x-total-count"] || String(r.data.length), 10);
      setPickups(r.data);
      setTotalCount(total);
      setCache(url, r.data, total);
    }).catch(() => {});
    axios.get(`${API}/admin/drivers`, h).then((r) => setDrivers(r.data)).catch(() => {});
    axios.get(`${API}/admin/clients`, h).then((r) => setClients(r.data)).catch(() => {});
    axios.get(`${API}/admin/driver-locations`, h).then((r) => setDriverLocations(r.data)).catch(() => {});
  };

  const loadMore = async () => {
    setLoadingMore(true);
    const nextCount = loadedCount + PAGE_SIZE;
    try {
      const url = buildUrl(nextCount);
      const r = await axios.get(url, authHeaders());
      const total = parseInt(r.headers["x-total-count"] || String(r.data.length), 10);
      setPickups(r.data);
      setTotalCount(total);
      setLoadedCount(nextCount);
      setCache(url, r.data, total);
    } catch {
      toast.error("Errore nel caricamento dei ritiri successivi");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setLoadedCount(PAGE_SIZE); // reset window on month change
  }, [filterMonth]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [filterMonth, loadedCount]);

  const selectedDriverLocation = useMemo(() => driverLocations.find((loc) => loc.driver_id === sortDriverId) || null, [driverLocations, sortDriverId]);

  // Upload/Delete DDT PDF files (admin) — max 5MB per file, PDF only
  const uploadDdt = async (pickupId, files) => {
    if (!files || files.length === 0) return;
    try {
      const encoded = [];
      for (const f of files) {
        if (f.type !== "application/pdf") { toast.error(`${f.name}: solo file PDF`); return; }
        if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name}: max 5MB`); return; }
        const buf = await f.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let binary = "";
        for (let i = 0; i < bytes.length; i += 0x8000) {
          binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
        }
        const b64 = btoa(binary);
        encoded.push({ filename: f.name, content_type: "application/pdf", data_base64: b64 });
      }
      await axios.post(`${API}/admin/pickups/${pickupId}/ddt`, { files: encoded }, authHeaders());
      toast.success(`${encoded.length} DDT caricat${encoded.length === 1 ? "o" : "i"}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore caricamento DDT"); }
  };

  const deleteDdt = async (pickupId, fileId) => {
    if (!window.confirm("Rimuovere questo DDT?")) return;
    try {
      await axios.delete(`${API}/admin/pickups/${pickupId}/ddt/${fileId}`, authHeaders());
      toast.success("DDT rimosso");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const filteredPickups = useMemo(() => {
    let result = pickups;
    if (filterMonth) result = result.filter((p) => p.created_at?.startsWith(filterMonth));
    if (searchTracking.trim()) {
      const q = searchTracking.trim().toLowerCase();
      result = result.filter((p) => p.tracking_number?.toLowerCase().includes(q));
    }
    return result;
  }, [pickups, filterMonth, searchTracking]);

  const orderedPickups = useMemo(() => {
    if (!selectedDriverLocation) return filteredPickups;
    return [...filteredPickups].sort((a, b) => {
      const aCoords = a.origin_coords;
      const bCoords = b.origin_coords;
      const aDist = aCoords?.length === 2 ? haversineKm(selectedDriverLocation.lat, selectedDriverLocation.lng, aCoords[0], aCoords[1]) : null;
      const bDist = bCoords?.length === 2 ? haversineKm(selectedDriverLocation.lat, selectedDriverLocation.lng, bCoords[0], bCoords[1]) : null;
      if (aDist === null && bDist === null) return 0;
      if (aDist === null) return 1;
      if (bDist === null) return -1;
      return aDist - bDist;
    });
  }, [filteredPickups, selectedDriverLocation]);

  const getDistanceLabel = (pickup) => {
    if (!selectedDriverLocation || !pickup.origin_coords?.length) return null;
    const km = haversineKm(selectedDriverLocation.lat, selectedDriverLocation.lng, pickup.origin_coords[0], pickup.origin_coords[1]);
    return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
  };

  const updateStatus = async (e) => {
    e.preventDefault();
    if (!statusForm.status) { toast.error("Seleziona uno stato"); return; }
    try {
      const res = await axios.post(`${API}/admin/pickups/${showStatus.id}/status`, statusForm, authHeaders());
      const emailTo = res.data.email_sent_to;
      if (emailTo) {
        toast.success(`Stato aggiornato! Email inviata a ${emailTo}`);
      } else {
        toast.success("Stato aggiornato");
      }
      setShowStatus(null);
      setStatusForm({ status: "", location: "", note: "" });
      load();
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const deletePickup = async (id, tracking) => {
    if (!window.confirm(`Eliminare il ritiro ${tracking}?`)) return;
    try {
      await axios.delete(`${API}/admin/pickups/${id}`, authHeaders());
      toast.success("Ritiro eliminato");
      load();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const startEditPickup = (p) => {
    const od = p.origin_details || {};
    const dd = p.dest_details || {};
    setEditItem(p);
    setEditForm({
      client_name: p.client_name || "", client_email: p.client_email || "", company: p.company || "",
      origin_name: od.name || "", origin_company: od.company || "", origin_address: od.address || "",
      origin_city: od.city || "", origin_cap: od.cap || "", origin_province: od.province || "", origin_phone: od.phone || "",
      dest_name: dd.name || "", dest_company: dd.company || "", dest_address: dd.address || "",
      dest_city: dd.city || "", dest_cap: dd.cap || "", dest_province: dd.province || "", dest_phone: dd.phone || "", dest_email: p.dest_email || "",
      service_type: p.service_type || "", urgency: p.urgency || "normale", urgency_cost: p.urgency_cost || "", note: p.note || "",
      ddt_reference: p.ddt_reference || "", scheduled_date: p.scheduled_date || "",
      descrizione_merce: p.descrizione_merce || "",
      insurance: p.insurance || false, insurance_value: p.insurance_value || "",
      costo: p.costo || "",
      colli: p.colli || [{ tipo: "collo", peso: "", dimensioni: "", quantita: 1 }],
      extra_services: p.extra_services || {},
    });
  };

  const saveEditPickup = async () => {
    try {
      const payload = {
        client_name: editForm.client_name, client_email: editForm.client_email, company: editForm.company,
        origin_details: { name: editForm.origin_name, address: editForm.origin_address, city: editForm.origin_city, cap: editForm.origin_cap, province: editForm.origin_province, phone: editForm.origin_phone },
        dest_details: { name: editForm.dest_name, address: editForm.dest_address, city: editForm.dest_city, cap: editForm.dest_cap, province: editForm.dest_province, phone: editForm.dest_phone },
        dest_email: (editForm.dest_email || "").trim().toLowerCase(),
        origin: editForm.origin_city, destination: editForm.dest_city,
        service_type: editForm.service_type, urgency: editForm.urgency, note: editForm.note,
        urgency_cost: editForm.urgency === "urgente" ? (parseFloat(editForm.urgency_cost) || 0) : 0,
        ddt_reference: editForm.ddt_reference, scheduled_date: editForm.scheduled_date,
        descrizione_merce: editForm.descrizione_merce,
        insurance: editForm.insurance, insurance_value: editForm.insurance ? editForm.insurance_value : "",
        costo: editForm.costo, colli: editForm.colli,
        extra_services: editForm.extra_services || {},
      };
      await axios.put(`${API}/admin/pickups/${editItem.id}`, payload, authHeaders());
      toast.success("Ritiro aggiornato");
      setEditItem(null);
      load();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const assignDriver = async (pickupId, driverId) => {
    try {
      const res = await axios.post(`${API}/admin/pickups/${pickupId}/assign`, { driver_id: driverId }, authHeaders());
      toast.success(`Assegnato a ${res.data.driver_name}`);
      load();
    } catch {
      toast.error("Errore nell'assegnazione");
    }
  };

  const handleClientSelect = (email) => {
    const client = clients.find((c) => c.email === email);
    if (client) {
      setForm({ ...form, client_email: email, client_name: client.name, company: client.company || "" });
    } else {
      setForm({ ...form, client_email: email });
    }
  };

  const addCollo = () => setForm({ ...form, colli: [...form.colli, { tipo: "collo", peso: "", dimensioni: "", quantita: 1 }] });
  const removeCollo = (i) => setForm({ ...form, colli: form.colli.filter((_, idx) => idx !== i) });
  const updateCollo = (i, field, value) => {
    const updated = [...form.colli];
    updated[i] = { ...updated[i], [field]: value };
    setForm({ ...form, colli: updated });
  };

  const createPickup = async (e) => {
    e.preventDefault();
    if (!form.origin_city || !form.dest_city) { toast.error("Compila almeno le citta di origine e destinazione"); return; }
    if (goodsReadyMode === "ready_at" && !goodsReadyTime) { toast.error("Inserisci l'orario per 'Merce pronta alle'"); return; }
    if (insurance && !insuranceValue) { toast.error("Inserisci il valore della merce da assicurare"); return; }
    setCreating(true);
    try {
      const payload = {
        ...form,
        goods_ready_status: goodsReadyMode !== "none" ? "pronta" : "non_pronta",
        goods_ready_time: goodsReadyMode === "ready_at" ? goodsReadyTime : "",
        insurance,
        insurance_value: insurance ? insuranceValue : "",
        urgency_cost: form.urgency === "urgente" ? (parseFloat(form.urgency_cost) || 0) : 0,
      };
      const res = await axios.post(`${API}/admin/pickups/create`, payload, authHeaders());
      toast.success(`Ritiro ${res.data.tracking_number} creato!`);
      setShowCreate(false);
      setForm({ ...emptyForm });
      setGoodsReadyMode("none");
      setGoodsReadyTime("");
      setInsurance(false);
      setInsuranceValue("");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Errore nella creazione"));
    } finally {
      setCreating(false);
    }
  };

  const toggleSelection = (pickupId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pickupId)) next.delete(pickupId);
      else next.add(pickupId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === orderedPickups.length) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(orderedPickups.map((p) => p.id)));
  };

  const printSelected = () => {
    const toPrint = orderedPickups.filter((p) => selectedIds.has(p.id));
    if (toPrint.length === 0) {
      toast.error("Seleziona almeno un ritiro");
      return;
    }
    printMultipleInternalLabels(toPrint);
  };

  const allSelected = orderedPickups.length > 0 && selectedIds.size === orderedPickups.length;

  return (
    <div data-testid="admin-pickups">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-zinc-500" data-testid="pickups-count-label">
            {orderedPickups.length}
            {(totalCount > orderedPickups.length || filterMonth || searchTracking) && ` di ${totalCount || pickups.length}`}
            {" ritiri"}
            {(filterMonth || searchTracking) ? " filtrati" : " totali"}
          </p>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input type="text" value={searchTracking} onChange={(e) => setSearchTracking(e.target.value)}
              placeholder="Cerca tracking..."
              className="h-10 pl-9 pr-3 border border-zinc-200 rounded-sm text-xs font-bold text-zinc-600 bg-white w-44 focus:border-[#FF5200] outline-none"
              data-testid="pickups-search-tracking" />
          </div>
          <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}
            className="h-10 px-3 border border-zinc-200 rounded-sm text-xs font-bold text-zinc-600 bg-white"
            data-testid="pickups-month-filter" />
          {filterMonth && (
            <button onClick={() => setFilterMonth("")} className="text-xs font-bold text-red-500 hover:text-red-700 uppercase tracking-wide" data-testid="pickups-clear-month-filter">
              Rimuovi filtro
            </button>
          )}
          {searchTracking && (
            <button onClick={() => setSearchTracking("")} className="text-xs font-bold text-red-500 hover:text-red-700 uppercase tracking-wide" data-testid="pickups-clear-search">
              Rimuovi ricerca
            </button>
          )}
          <select value={sortDriverId} onChange={(e) => setSortDriverId(e.target.value)} className="h-10 px-3 border border-zinc-200 rounded-sm text-xs font-bold uppercase tracking-wide text-zinc-600 bg-white" data-testid="pickups-proximity-driver-select">
            <option value="">Ordine attuale</option>
            {drivers.map((driver) => <option key={driver.id} value={driver.id}>{`${driver.name} ${driver.surname} (${driver.vehicle_plate})`}</option>)}
          </select>
          {selectedDriverLocation && (
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 text-[11px] font-bold uppercase tracking-wide text-purple-700 rounded-sm" data-testid="pickups-proximity-status">
              <Navigation size={13} /> Vicinanza attiva
            </div>
          )}
          {pickups.length > 0 && (
            <button
              onClick={toggleSelectAll}
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-zinc-200 text-xs font-bold uppercase tracking-wide rounded-sm hover:border-zinc-400"
              data-testid="toggle-all-pickups-btn"
            >
              {allSelected ? <CheckSquare size={15} className="text-[#FF5200]" /> : <Square size={15} className="text-zinc-500" />}
              {allSelected ? "Deseleziona tutto" : "Seleziona tutto"}
            </button>
          )}
          {selectedIds.size > 0 && (
            <button
              onClick={printSelected}
              className="inline-flex items-center gap-2 px-3 py-2 bg-[#09090B] text-white text-xs font-bold uppercase tracking-wide rounded-sm"
              data-testid="print-selected-pickups-btn"
            >
              <Printer size={15} /> Stampa etichette ({selectedIds.size})
            </button>
          )}
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors"
          data-testid="create-pickup-btn"
        >
          <Plus size={16} /> Nuovo Ritiro
        </button>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-left" data-testid="pickups-table">
          <thead className="border-b border-zinc-200 bg-zinc-50">
            <tr>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Sel.</th>
              <th className="px-6 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Tracking</th>
              <th className="px-6 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Partenza</th>
              <th className="px-6 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Destinazione</th>
              <th className="px-6 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Data</th>
              <th className="px-6 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Colli</th>
              <th className="px-6 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Servizio</th>
              <th className="px-6 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Stato</th>
              <th className="px-6 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Autista</th>
              <th className="px-6 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {pickups.length === 0 ? (
              <tr><td colSpan={10} className="px-6 py-12 text-center">
                <Truck size={40} className="text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-500">Nessun ritiro</p>
              </td></tr>
            ) : (
              orderedPickups.map((p) => (
                <React.Fragment key={p.id}>
                <tr className="hover:bg-zinc-50 cursor-pointer" data-testid={`pickup-row-${p.tracking_number}`}
                  onClick={() => setExpandedRow(expandedRow === p.id ? null : p.id)}>
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleSelection(p.id)}
                      className="text-zinc-500 hover:text-[#FF5200]"
                      data-testid={`select-pickup-${p.tracking_number}`}
                    >
                      {selectedIds.has(p.id) ? <CheckSquare size={18} className="text-[#FF5200]" /> : <Square size={18} />}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      {expandedRow === p.id ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
                      <div>
                        <p className="font-bold text-sm font-heading text-[#09090B]">{p.tracking_number}</p>
                        <p className="text-xs text-zinc-400">{p.created_at ? p.created_at.slice(0, 10).split("-").reverse().join("-") : ""}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const pickupPoint = getPickupPointPreview(p.origin_details, p.origin);
                      return (
                        <div data-testid={`pickup-origin-${p.tracking_number}`}>
                          <p className="text-sm font-bold text-[#09090B]">{pickupPoint.title}</p>
                          <p className="text-xs text-zinc-600">{pickupPoint.address}</p>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-xs text-zinc-500">{pickupPoint.locality}</p>
                            {getDistanceLabel(p) && <span className="text-[10px] font-bold text-purple-700" data-testid={`pickup-distance-${p.tracking_number}`}>{getDistanceLabel(p)}</span>}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            {getReadyBadgeLabel(p) && <span className="px-2 py-1 bg-green-50 text-green-700 text-[11px] font-bold rounded-sm" data-testid={`admin-ready-badge-${p.tracking_number}`}>{getReadyBadgeLabel(p)}</span>}
                            {getDdtBadgeLabel(p) && <button type="button" onClick={() => downloadPdf(p.ddt_files[0])} className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-700 text-[11px] font-bold rounded-sm hover:bg-zinc-200" data-testid={`admin-ddt-badge-${p.tracking_number}`}><Paperclip size={12} /> {getDdtBadgeLabel(p)}</button>}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const dd = p.dest_details;
                      const destCity = dd ? `${dd.city || ""}${dd.province ? ` (${dd.province})` : ""}` : (p.destination || "-");
                      const destAddr = dd ? (dd.address || "") : "";
                      const destName = dd ? (dd.name || dd.company || "") : "";
                      return (
                        <div data-testid={`pickup-destination-${p.tracking_number}`}>
                          {destName && <p className="text-sm font-bold text-[#09090B]">{destName}</p>}
                          {destAddr && <p className="text-xs text-zinc-600">{destAddr}</p>}
                          <p className="text-xs text-zinc-500">{destCity}</p>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-600">{p.scheduled_date ? p.scheduled_date.split("-").reverse().join("-") : "-"}</td>
                  <td className="px-6 py-4 text-sm text-zinc-600">
                    {p.colli ? `${p.colli.reduce((sum, c) => sum + (parseInt(c.quantita) || 1), 0)} ${p.colli.reduce((sum, c) => sum + (parseInt(c.quantita) || 1), 0) === 1 ? "collo" : "colli"}` : "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-600">
                    <div className="capitalize">{p.service_type || "-"}</div>
                    {(() => { const u = urgencyLabel(p.urgency); return <span className={`mt-1 inline-block px-1.5 py-0.5 text-[10px] font-bold rounded-sm ${u.cls}`}>{u.label}</span>; })()}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-bold uppercase tracking-wide rounded-sm ${statusStyle(p.status)}`}>
                      {statusLabel(p.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={p.driver_id || ""}
                      onChange={(e) => { if (e.target.value) assignDriver(p.id, e.target.value); }}
                      className={`text-xs font-bold px-2 py-1.5 rounded-sm border cursor-pointer ${p.driver_id ? "border-purple-200 bg-purple-50 text-purple-700" : "border-zinc-200 text-zinc-400"}`}
                      data-testid={`assign-driver-${p.tracking_number}`}
                    >
                      <option value="">{p.driver_name || "Non assegnato"}</option>
                      {drivers.map((d) => (
                        <option key={d.id} value={d.id}>{`${d.name} ${d.surname} (${d.vehicle_plate})`}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEditPickup(p)}
                        className="p-1 text-zinc-400 hover:text-blue-600"
                        title="Modifica"
                        data-testid={`edit-pickup-${p.tracking_number}`}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => printInternalLabel(p, { kind: "pickup", useServer: true })}
                        className="p-1 text-zinc-400 hover:text-[#09090B]"
                        title="Stampa etichetta"
                        data-testid={`admin-print-pickup-${p.tracking_number}`}
                      >
                        <Printer size={14} />
                      </button>
                      {p.pod && (
                        <button
                          onClick={() => downloadPodPdf(p.tracking_number)}
                          className="p-1 text-green-600 hover:text-green-800"
                          title="Scarica POD PDF"
                          data-testid={`admin-download-pod-${p.tracking_number}`}
                        >
                          <Download size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => { setShowStatus(p); setStatusForm({ status: "", location: "", note: "" }); }}
                        className="text-xs font-bold text-[#FF5200] hover:underline uppercase"
                        data-testid={`update-pickup-status-${p.tracking_number}`}
                      >
                        Aggiorna
                      </button>
                      <button onClick={() => deletePickup(p.id, p.tracking_number)} className="p-1 text-red-400 hover:text-red-600" data-testid={`delete-pickup-${p.tracking_number}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRow === p.id && (
                  <tr><td colSpan={9} className="bg-zinc-50 px-6 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {p.origin_details && (
                        <div className="bg-white p-3 border border-zinc-200 rounded-sm">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1"><User size={10} /> Ritiro da</p>
                          <p className="text-sm font-bold">{p.origin_details.name}</p>
                          {p.origin_details.company && <p className="text-xs text-zinc-600">{p.origin_details.company}</p>}
                          <p className="text-xs text-zinc-600">{p.origin_details.address}, {p.origin_details.cap} {p.origin_details.city}</p>
                          {p.origin_details.phone && <p className="text-xs text-blue-600 flex items-center gap-1 mt-1"><Phone size={10} /> {p.origin_details.phone}</p>}
                        </div>
                      )}
                      {p.dest_details && (
                        <div className="bg-white p-3 border border-zinc-200 rounded-sm">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1"><MapPin size={10} /> Consegna a</p>
                          <p className="text-sm font-bold">{p.dest_details.name}</p>
                          {p.dest_details.company && <p className="text-xs text-zinc-600">{p.dest_details.company}</p>}
                          <p className="text-xs text-zinc-600">{p.dest_details.address}, {p.dest_details.cap} {p.dest_details.city}</p>
                          {p.dest_details.phone && <p className="text-xs text-blue-600 flex items-center gap-1 mt-1"><Phone size={10} /> {p.dest_details.phone}</p>}
                        </div>
                      )}
                      <div className="bg-white p-3 border border-zinc-200 rounded-sm">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1"><Package size={10} /> Colli ({p.colli?.reduce((sum, c) => sum + (parseInt(c.quantita) || 1), 0) || 0})</p>
                        {p.colli?.map((c, i) => (
                          <div key={`${p.id}-collo-${i}`} className="flex justify-between text-xs py-1 border-b border-zinc-100 last:border-0">
                            <span className="font-bold">{c.tipo === "pallet" ? "PLT" : "CLO"} {c.quantita > 1 ? `x${c.quantita}` : `${i + 1}`}</span>
                            <span>{c.peso ? `${c.peso} kg` : "-"} {c.dimensioni ? `| ${c.dimensioni}` : ""}</span>
                          </div>
                        ))}
                        {p.note && <p className="text-xs text-amber-700 mt-2 bg-amber-50 p-2 rounded">Note: {p.note}</p>}
                      {p.dest_email && <p className="text-xs text-sky-700 mt-2" data-testid={`dest-email-${p.tracking_number}`}>Notifiche destinatario: {p.dest_email}</p>}
                        {p.descrizione_merce && <p className="text-xs text-zinc-700 mt-2 bg-zinc-50 p-2 rounded"><span className="font-bold">Merce:</span> {p.descrizione_merce}</p>}
                        {(() => { const u = urgencyLabel(p.urgency); return <p className="mt-2"><span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded-sm ${u.cls}`}>{u.label}</span> <span className="text-[10px] text-zinc-500 ml-1 uppercase font-bold">{p.service_type}</span></p>; })()}
                        {p.distinta_code && <p className="text-xs text-purple-600 mt-2 font-bold">Distinta: {p.distinta_code}</p>}
                        {p.scheduled_date && <p className="text-xs text-[#FF5200] mt-2 font-bold">Data: {p.scheduled_date.split("-").reverse().join("-")}</p>}
                        {p.insurance && <p className="text-xs text-blue-600 mt-2 font-bold">Assicurata: €{p.insurance_value || "-"}</p>}
                        {hasAnyExtra(p) && (
                          <div className="mt-2"><ExtraServicesBadges value={p.extra_services} item={p} /></div>
                        )}
                      </div>
                    </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="bg-white p-3 border border-zinc-200 rounded-sm">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Merce pronta</p>
                      <p className="text-sm font-bold text-[#09090B]">{getReadyBadgeLabel(p) || "Non pronta"}</p>
                    </div>
                    <CostField itemId={p.id} currentCost={p.costo} extras={p.extra_services} type="pickups" onUpdate={load} />
                    <div className="bg-white p-3 border border-zinc-200 rounded-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">DDT PDF</p>
                        <label className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-purple-600 cursor-pointer hover:underline" data-testid={`upload-ddt-label-${p.tracking_number}`}>
                          <Paperclip size={11} /> Carica
                          <input type="file" accept="application/pdf" multiple className="hidden" onChange={(e) => { uploadDdt(p.id, e.target.files); e.target.value = ""; }} data-testid={`admin-upload-ddt-input-${p.tracking_number}`} />
                        </label>
                      </div>
                      {p.ddt_files?.length ? p.ddt_files.map((file) => (
                        <div key={file.id} className="flex items-center gap-2 mb-2 last:mb-0">
                          <button onClick={() => downloadPdf(file)} className="flex-1 flex items-center justify-between text-left text-xs px-3 py-2 border border-zinc-200 rounded-sm hover:border-purple-500" data-testid={`admin-download-ddt-${file.id}`}>
                            <span className="truncate pr-3">{file.filename}</span>
                            <Printer size={14} className="text-zinc-400" />
                          </button>
                          <button onClick={() => deleteDdt(p.id, file.id)} className="p-2 text-red-500 hover:bg-red-50 border border-red-200 rounded-sm" title="Rimuovi DDT" data-testid={`admin-delete-ddt-${file.id}`}><Trash2 size={12} /></button>
                        </div>
                      )) : <p className="text-xs text-zinc-500">Nessun DDT caricato.</p>}
                    </div>
                  </div>
                    {/* POD */}
                    {p.pod && (
                      <div className="mt-4 bg-green-50 border border-green-200 rounded-sm p-4">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-green-600 mb-2 flex items-center gap-1"><PenLine size={10} /> Prova di Consegna (POD)</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div><p className="text-[10px] text-zinc-400 uppercase">Firmato da</p><p className="text-sm font-bold">{p.pod.signed_by}</p></div>
                          <div><p className="text-[10px] text-zinc-400 uppercase">Data/Ora</p><p className="text-sm font-bold">{formatDateTimeIT(p.pod.timestamp)}</p></div>
                          <div><p className="text-[10px] text-zinc-400 uppercase">Autista</p><p className="text-sm font-bold">{p.pod.driver_name || p.driver_name}</p></div>
                          {p.pod.geolocation?.lat && <div><p className="text-[10px] text-zinc-400 uppercase">GPS</p><p className="text-xs">{p.pod.geolocation.lat?.toFixed(4)}, {p.pod.geolocation.lng?.toFixed(4)}</p></div>}
                        </div>
                        <div className="flex gap-4 mt-3">
                          {p.pod.photo && <div><p className="text-[10px] text-zinc-400 uppercase mb-1">Foto</p><img src={p.pod.photo} alt="POD" className="max-h-28 rounded border" /></div>}
                          {p.pod.signature && <div><p className="text-[10px] text-zinc-400 uppercase mb-1">Firma</p><img src={p.pod.signature} alt="Firma" className="max-h-16 bg-white rounded border p-1" /></div>}
                        </div>
                        {p.pod.note && <p className="text-xs text-green-700 mt-2">Note: {p.pod.note}</p>}
                        <div className="mt-3 pt-3 border-t border-green-200">
                          <button
                            onClick={() => downloadPodPdf(p.tracking_number)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-green-700"
                            data-testid={`admin-detail-download-pod-${p.tracking_number}`}
                          >
                            <Download size={14} /> Scarica POD PDF
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Anomaly */}
                    {p.anomaly && (
                      <div className="mt-4 bg-red-50 border border-red-200 rounded-sm p-4">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-red-600 mb-2 flex items-center gap-1"><AlertTriangle size={10} /> Anomalia Segnalata</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div><p className="text-[10px] text-zinc-400 uppercase">Tipo</p><p className="text-sm font-bold text-red-800">{p.anomaly.type}</p></div>
                          <div><p className="text-[10px] text-zinc-400 uppercase">Data/Ora</p><p className="text-sm">{formatDateTimeIT(p.anomaly.timestamp)}</p></div>
                          <div><p className="text-[10px] text-zinc-400 uppercase">Autista</p><p className="text-sm">{p.anomaly.driver_name || p.driver_name}</p></div>
                        </div>
                        {p.anomaly.note && <p className="text-xs text-red-700 mt-2">Note: {p.anomaly.note}</p>}
                        {p.anomaly.photo && <img src={p.anomaly.photo} alt="Anomalia" className="max-h-28 rounded border mt-2" />}
                      </div>
                    )}
                    {/* Status History */}
                    {p.status_history?.length > 0 && (
                      <div className="mt-4 bg-white border border-zinc-200 rounded-sm p-4">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1"><Clock size={10} /> Cronologia Stati</p>
                        <div className="space-y-1.5">
                          {[...p.status_history].reverse().map((ev, i) => (
                            <div key={`${p.id}-hist-${ev.timestamp}-${i}`} className="flex items-start gap-2 text-xs">
                              <span className="text-zinc-400 whitespace-nowrap">{formatDateTimeIT(ev.timestamp)}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${statusStyle(ev.status)}`}>{statusLabel(ev.status)}</span>
                              {ev.location && <span className="text-zinc-500">{ev.location}</span>}
                              {ev.note && <span className="text-zinc-600 italic">{ev.note}</span>}
                              {ev.updated_by && <span className="text-zinc-400">- {ev.updated_by}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </td></tr>
                )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalCount > pickups.length && !searchTracking && (
        <div className="flex justify-center mt-6">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 border border-zinc-300 bg-white px-5 py-2 text-xs font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 rounded-sm"
            data-testid="pickups-load-more-btn"
          >
            {loadingMore ? <Loader2 size={14} className="animate-spin" /> : <ChevronDown size={14} />}
            Carica altri {Math.min(PAGE_SIZE, totalCount - pickups.length)} di {totalCount - pickups.length} rimanenti
          </button>
        </div>
      )}

      {showStatus && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="update-pickup-status-modal">
          <div className="bg-white w-full max-w-md">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold uppercase">Aggiorna Stato Ritiro</h3>
                <p className="text-sm text-zinc-500">{showStatus.tracking_number} | {showStatus.origin || "-"} &rarr; {showStatus.destination || "-"}</p>
              </div>
              <button onClick={() => setShowStatus(null)} className="p-2 text-zinc-400 hover:text-zinc-600"><X size={20} /></button>
            </div>
            <form onSubmit={updateStatus} className="p-6 space-y-4">
              <div>
                <label htmlFor="pickup-status" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Nuovo Stato *</label>
                <select id="pickup-status" value={statusForm.status} onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none bg-white" data-testid="pickup-status-select">
                  <option value="">Seleziona stato</option>
                  {statusOptions.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                </select>
              </div>
              <div>
                <label htmlFor="pickup-location" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Localita</label>
                <input id="pickup-location" type="text" value={statusForm.location} onChange={(e) => setStatusForm({ ...statusForm, location: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" placeholder="es. Bologna" data-testid="pickup-status-location" />
              </div>
              <div>
                <label htmlFor="pickup-note" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Note</label>
                <input id="pickup-note" type="text" value={statusForm.note} onChange={(e) => setStatusForm({ ...statusForm, note: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" placeholder="Dettagli aggiornamento" data-testid="pickup-status-note" />
              </div>
              <button type="submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5200] text-white h-10 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm"
                data-testid="pickup-status-update-submit">
                Aggiorna Stato
              </button>
              <p className="flex items-center gap-2 text-xs text-zinc-500 mt-3">
                <Mail size={14} className="text-[#FF5200]" />
                Il cliente ricevera una email di notifica automatica con il nuovo stato
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Create Pickup Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-6 overflow-auto" data-testid="create-pickup-modal">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto mb-6">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-heading text-lg font-bold uppercase">Nuovo Ritiro</h3>
              <button onClick={() => { setShowCreate(false); setForm({ ...emptyForm }); }} className="p-2 text-zinc-400 hover:text-zinc-600"><X size={20} /></button>
            </div>
            <form onSubmit={createPickup} className="p-6 space-y-5">
              {/* Cliente e DDT */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Cliente</label>
                  <select value={form.client_email} onChange={(e) => handleClientSelect(e.target.value)}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" data-testid="pickup-client-select">
                    <option value="">Seleziona cliente</option>
                    {clients.map((c) => (<option key={c.email} value={c.email}>{c.name} ({c.email})</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Rif. DDT</label>
                  <input type="text" value={form.ddt_reference} onChange={(e) => setForm({ ...form, ddt_reference: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" placeholder="es. DDT-2024/001" data-testid="pickup-ddt" />
                </div>
              </div>

              {/* Ritiro da */}
              <fieldset className="border border-zinc-200 rounded-sm p-4">
                <legend className="text-xs font-bold tracking-widest uppercase text-zinc-500 px-2 font-heading flex items-center gap-1"><User size={12} /> Ritiro da</legend>
                <div className="mt-1 mb-3">
                  <AddressBookPicker prefix="origin" label="Mittenti" form={form} setForm={setForm} clientEmail={form.client_email} />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <input type="text" value={form.origin_name} onChange={(e) => setForm({ ...form, origin_name: e.target.value })} placeholder="Nome / Ragione sociale *" className="col-span-2 h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" data-testid="pickup-origin-name" />
                  <input type="text" value={form.origin_address} onChange={(e) => setForm({ ...form, origin_address: e.target.value })} placeholder="Indirizzo *" className="col-span-2 h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" data-testid="pickup-origin-address" />
                  <input type="text" value={form.origin_city} onChange={(e) => setForm({ ...form, origin_city: e.target.value })} placeholder="Citta *" className="h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" data-testid="pickup-origin-city" />
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" value={form.origin_cap} onChange={(e) => setForm({ ...form, origin_cap: e.target.value })} placeholder="CAP" className="h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" />
                    <input type="text" value={form.origin_province} onChange={(e) => setForm({ ...form, origin_province: e.target.value })} placeholder="Prov." className="h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" />
                  </div>
                  <input type="text" value={form.origin_phone} onChange={(e) => setForm({ ...form, origin_phone: e.target.value })} placeholder="Telefono" className="col-span-2 h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" />
                </div>
              </fieldset>

              {/* Consegna a */}
              <fieldset className="border border-zinc-200 rounded-sm p-4">
                <legend className="text-xs font-bold tracking-widest uppercase text-zinc-500 px-2 font-heading flex items-center gap-1"><MapPin size={12} /> Consegna a</legend>
                <div className="mt-1 mb-3">
                  <AddressBookPicker prefix="dest" label="Destinatari" form={form} setForm={setForm} clientEmail={form.client_email} />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <input type="text" value={form.dest_name} onChange={(e) => setForm({ ...form, dest_name: e.target.value })} placeholder="Nome / Ragione sociale *" className="col-span-2 h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" data-testid="pickup-dest-name" />
                  <input type="text" value={form.dest_address} onChange={(e) => setForm({ ...form, dest_address: e.target.value })} placeholder="Indirizzo *" className="col-span-2 h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" data-testid="pickup-dest-address" />
                  <input type="text" value={form.dest_city} onChange={(e) => setForm({ ...form, dest_city: e.target.value })} placeholder="Citta *" className="h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" data-testid="pickup-dest-city" />
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" value={form.dest_cap} onChange={(e) => setForm({ ...form, dest_cap: e.target.value })} placeholder="CAP" className="h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" />
                    <input type="text" value={form.dest_province} onChange={(e) => setForm({ ...form, dest_province: e.target.value })} placeholder="Prov." className="h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" />
                  </div>
                  <input type="text" value={form.dest_phone} onChange={(e) => setForm({ ...form, dest_phone: e.target.value })} placeholder="Telefono" className="col-span-2 h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" />
                  <input type="email" value={form.dest_email} onChange={(e) => setForm({ ...form, dest_email: e.target.value })} placeholder="Email destinatario (notifiche creazione / ritiro / in consegna)" className="col-span-2 h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" data-testid="admin-dest-email" />
                </div>
              </fieldset>

              {/* Colli */}
              <fieldset className="border border-zinc-200 rounded-sm p-4">
                <legend className="text-xs font-bold tracking-widest uppercase text-zinc-500 px-2 font-heading flex items-center gap-1"><Package size={12} /> Colli ({form.colli.reduce((sum, c) => sum + (parseInt(c.quantita) || 1), 0)})</legend>
                <div className="space-y-2 mt-2">
                  {form.colli.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <select value={c.tipo} onChange={(e) => updateCollo(i, "tipo", e.target.value)} className="h-9 px-2 border border-zinc-200 rounded-sm text-sm w-28">
                        <option value="collo">Collo</option>
                        <option value="pallet">Pallet</option>
                      </select>
                      <input type="number" min="1" value={c.quantita} onChange={(e) => updateCollo(i, "quantita", parseInt(e.target.value) || 1)} placeholder="N." className="h-9 px-2 border border-zinc-200 rounded-sm text-sm w-20 focus:border-[#FF5200] outline-none" data-testid={`pickup-collo-quantita-${i}`} />
                      <input type="text" value={c.peso} onChange={(e) => updateCollo(i, "peso", e.target.value)} placeholder="Peso (kg)" className="h-9 px-3 border border-zinc-200 rounded-sm text-sm flex-1 focus:border-[#FF5200] outline-none" />
                      <input type="text" value={c.dimensioni} onChange={(e) => updateCollo(i, "dimensioni", e.target.value)} placeholder="LxPxH (cm)" className="h-9 px-3 border border-zinc-200 rounded-sm text-sm flex-1 focus:border-[#FF5200] outline-none" />
                      {form.colli.length > 1 && <button type="button" onClick={() => removeCollo(i)} className="p-1 text-red-400 hover:text-red-600"><Trash2 size={14} /></button>}
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addCollo} className="mt-2 text-xs font-bold text-[#FF5200] hover:underline uppercase flex items-center gap-1" data-testid="pickup-add-collo-btn"><Plus size={12} /> Aggiungi collo</button>
              </fieldset>

              {/* Servizio, urgenza, data, note */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Servizio</label>
                  <select value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none bg-white">
                    <option value="standard">Standard</option>
                    <option value="express">Express</option>
                    <option value="dedicato">Dedicato</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Urgenza</label>
                  <select value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none bg-white"
                    data-testid="admin-pick-urgency">
                    <option value="normale">Normale</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>
                <UrgencyCostInput urgency={form.urgency} value={form.urgency_cost} onChange={(v) => setForm((f) => ({ ...f, urgency_cost: v }))} testId="admin-pick-urgency-cost" />
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Data</label>
                  <input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none bg-white"
                    data-testid="admin-pickup-scheduled-date" />
                </div>
              </div>

              {/* Assicurazione */}
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Assicurazione</label>
                <div className="flex items-stretch gap-0">
                  <button type="button"
                    onClick={() => { setInsurance(!insurance); if (insurance) setInsuranceValue(""); }}
                    className={`flex-shrink-0 h-10 px-4 border-2 rounded-l-sm text-xs font-bold uppercase tracking-wide transition-colors ${insurance ? "border-[#FF5200] bg-[#FF5200] text-white" : "border-zinc-200 text-zinc-600 hover:border-[#FF5200]"}`}
                    data-testid="admin-pickup-insurance-toggle">
                    {insurance ? "Assicurata" : "Non assicurata"}
                  </button>
                  <input type="text" inputMode="decimal" value={insuranceValue}
                    onChange={(e) => setInsuranceValue(e.target.value)}
                    disabled={!insurance}
                    placeholder="Valore merce (EUR)"
                    className={`flex-1 h-10 px-3 border-2 border-l-0 rounded-r-sm text-sm outline-none ${insurance ? "border-[#FF5200] bg-white" : "border-zinc-200 bg-zinc-50"} focus:border-[#FF5200] disabled:opacity-50`}
                    data-testid="admin-pickup-insurance-value" />
                </div>
              </div>

              {/* Merce Pronta */}
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">Merce pronta</label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button"
                    onClick={() => { setGoodsReadyMode("ready_now"); setGoodsReadyTime(""); }}
                    className={`h-10 px-4 border-2 rounded-sm text-xs font-bold uppercase tracking-wide transition-colors ${goodsReadyMode === "ready_now" ? "border-[#FF5200] bg-[#FF5200] text-white" : "border-zinc-200 text-zinc-600 hover:border-[#FF5200]"}`}
                    data-testid="admin-pickup-goods-ready-now-btn">
                    Merce pronta
                  </button>
                  <div className="flex items-stretch gap-0">
                    <button type="button"
                      onClick={() => setGoodsReadyMode("ready_at")}
                      className={`flex-shrink-0 px-3 border-2 border-r-0 rounded-l-sm text-xs font-bold uppercase tracking-wide transition-colors ${goodsReadyMode === "ready_at" ? "border-[#FF5200] bg-[#FF5200] text-white" : "border-zinc-200 text-zinc-600 hover:border-[#FF5200]"}`}
                      data-testid="admin-pickup-goods-ready-at-btn">
                      Pronta alle
                    </button>
                    <input type="time" value={goodsReadyTime}
                      onChange={(e) => { setGoodsReadyTime(e.target.value); setGoodsReadyMode("ready_at"); }}
                      disabled={goodsReadyMode !== "ready_at"}
                      className={`flex-1 h-10 px-2 border-2 rounded-r-sm text-sm outline-none ${goodsReadyMode === "ready_at" ? "border-[#FF5200]" : "border-zinc-200 bg-zinc-50"} focus:border-[#FF5200] disabled:opacity-50`}
                      data-testid="admin-pickup-goods-ready-time-input" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Dettaglio Merce</label>
                  <input type="text" value={form.descrizione_merce || ""} onChange={(e) => setForm({ ...form, descrizione_merce: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none"
                    placeholder="Es. componenti meccanici, mobili, ecc." data-testid="pickup-descrizione-merce" />
                </div>
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Note</label>
                  <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" placeholder="Note opzionali" />
                </div>
              </div>

              <ExtraServicesEditor
                value={form.extra_services}
                onChange={(es) => setForm({ ...form, extra_services: es })}
                testIdPrefix="create-pickup-extras"
              />

              <button type="submit" disabled={creating}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5200] text-white h-11 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm disabled:opacity-50"
                data-testid="pickup-create-submit">
                {creating ? <Loader2 size={16} className="animate-spin" /> : <Truck size={16} />} Crea Ritiro
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Pickup Modal */}
      {editItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-6 overflow-auto" data-testid="edit-pickup-modal">
          <div className="bg-white w-full max-w-3xl mb-6">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="text-sm font-bold tracking-widest uppercase font-heading">Modifica Ritiro {editItem.tracking_number}</h3>
              <button onClick={() => setEditItem(null)} className="p-1 text-zinc-400 hover:text-zinc-600"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-auto">
              <p className="text-xs font-bold tracking-widest uppercase text-zinc-500">Cliente</p>
              <div>
                <label className="block text-[10px] uppercase text-zinc-400 mb-1">Seleziona cliente</label>
                <select
                  value={editForm.client_email}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                      setEditForm({ ...editForm, client_email: "", client_name: "", company: "" });
                    } else {
                      const c = clients.find((cl) => cl.email === v);
                      setEditForm({ ...editForm, client_email: v, client_name: c?.name || editForm.client_name, company: c?.company || editForm.company });
                    }
                  }}
                  className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm"
                  data-testid="edit-pickup-client-select"
                >
                  <option value="">-- Nessun cliente assegnato --</option>
                  {clients.map((c) => (
                    <option key={c.email} value={c.email}>{c.name} {c.company ? `· ${c.company}` : ""} ({c.email})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Nome / Ragione sociale</label><input value={editForm.client_name} onChange={(e) => setEditForm({...editForm, client_name: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="edit-pickup-client-name" /></div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Email</label><input value={editForm.client_email} onChange={(e) => setEditForm({...editForm, client_email: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
              </div>
              <p className="text-xs font-bold tracking-widest uppercase text-zinc-500 pt-2">Mittente</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><label className="block text-[10px] uppercase text-zinc-400 mb-1">Nome / Ragione sociale</label><input value={editForm.origin_name} onChange={(e) => setEditForm({...editForm, origin_name: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Telefono</label><input value={editForm.origin_phone} onChange={(e) => setEditForm({...editForm, origin_phone: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Indirizzo</label><input value={editForm.origin_address} onChange={(e) => setEditForm({...editForm, origin_address: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Citta</label><input value={editForm.origin_city} onChange={(e) => setEditForm({...editForm, origin_city: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">CAP</label><input value={editForm.origin_cap} onChange={(e) => setEditForm({...editForm, origin_cap: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                  <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Prov.</label><input value={editForm.origin_province} onChange={(e) => setEditForm({...editForm, origin_province: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                </div>
              </div>
              <p className="text-xs font-bold tracking-widest uppercase text-zinc-500 pt-2">Destinatario</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><label className="block text-[10px] uppercase text-zinc-400 mb-1">Nome / Ragione sociale</label><input value={editForm.dest_name} onChange={(e) => setEditForm({...editForm, dest_name: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Telefono</label><input value={editForm.dest_phone} onChange={(e) => setEditForm({...editForm, dest_phone: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Email destinatario</label><input type="email" value={editForm.dest_email} onChange={(e) => setEditForm({...editForm, dest_email: e.target.value})} placeholder="notifiche al destinatario" className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="edit-dest-email" /></div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Indirizzo</label><input value={editForm.dest_address} onChange={(e) => setEditForm({...editForm, dest_address: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Citta</label><input value={editForm.dest_city} onChange={(e) => setEditForm({...editForm, dest_city: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">CAP</label><input value={editForm.dest_cap} onChange={(e) => setEditForm({...editForm, dest_cap: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                  <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Prov.</label><input value={editForm.dest_province} onChange={(e) => setEditForm({...editForm, dest_province: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                </div>
              </div>
              <p className="text-xs font-bold tracking-widest uppercase text-zinc-500 pt-2">Dettagli</p>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Servizio</label>
                  <select value={editForm.service_type} onChange={(e) => setEditForm({...editForm, service_type: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm">
                    <option value="trasporto">Trasporto</option><option value="express">Express</option><option value="standard">Standard</option><option value="groupage">Groupage</option>
                  </select>
                </div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Urgenza</label>
                  <select value={editForm.urgency} onChange={(e) => setEditForm({...editForm, urgency: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="edit-pick-urgency">
                    <option value="normale">Normale</option><option value="urgente">Urgente</option>
                  </select>
                </div>
                <UrgencyCostInput compact urgency={editForm.urgency} value={editForm.urgency_cost} onChange={(v) => setEditForm((f) => ({ ...f, urgency_cost: v }))} testId="edit-pick-urgency-cost" />
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Data</label><input type="date" value={editForm.scheduled_date} onChange={(e) => setEditForm({...editForm, scheduled_date: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Rif. DDT</label><input value={editForm.ddt_reference} onChange={(e) => setEditForm({...editForm, ddt_reference: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Costo (EUR)</label><input type="number" step="0.01" value={editForm.costo} onChange={(e) => setEditForm({...editForm, costo: e.target.value})} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Assicurazione</label>
                  <div className="flex items-center gap-2 h-9">
                    <button type="button" onClick={() => setEditForm({...editForm, insurance: !editForm.insurance, insurance_value: editForm.insurance ? "" : editForm.insurance_value})} className={`px-3 py-1 text-xs font-bold rounded-sm ${editForm.insurance ? "bg-[#FF5200] text-white" : "bg-zinc-100 text-zinc-600"}`}>{editForm.insurance ? "Si" : "No"}</button>
                    {editForm.insurance && <input value={editForm.insurance_value} onChange={(e) => setEditForm({...editForm, insurance_value: e.target.value})} placeholder="Valore EUR" className="flex-1 h-9 px-2 border border-zinc-200 rounded-sm text-sm" />}
                  </div>
                </div>
              </div>
              <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Dettaglio Merce</label><input value={editForm.descrizione_merce || ""} onChange={(e) => setEditForm({...editForm, descrizione_merce: e.target.value})} placeholder="Es. componenti meccanici, mobili" className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="edit-pickup-descrizione-merce" /></div>
              <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Note</label><textarea value={editForm.note} onChange={(e) => setEditForm({...editForm, note: e.target.value})} rows={2} className="w-full px-2 py-1.5 border border-zinc-200 rounded-sm text-sm" /></div>
              <p className="text-xs font-bold tracking-widest uppercase text-zinc-500 pt-2">Colli</p>
              {editForm.colli?.map((c, i) => (
                <div key={i} className="grid grid-cols-5 gap-2 items-end">
                  <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Tipo</label>
                    <select value={c.tipo} onChange={(e) => { const arr = [...editForm.colli]; arr[i] = {...arr[i], tipo: e.target.value}; setEditForm({...editForm, colli: arr}); }} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm">
                      <option value="collo">Collo</option><option value="pallet">Pallet</option><option value="busta">Busta</option><option value="altro">Altro</option>
                    </select>
                  </div>
                  <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Peso (kg)</label><input value={c.peso} onChange={(e) => { const arr = [...editForm.colli]; arr[i] = {...arr[i], peso: e.target.value}; setEditForm({...editForm, colli: arr}); }} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                  <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Dimensioni</label><input value={c.dimensioni} onChange={(e) => { const arr = [...editForm.colli]; arr[i] = {...arr[i], dimensioni: e.target.value}; setEditForm({...editForm, colli: arr}); }} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                  <div><label className="block text-[10px] uppercase text-zinc-400 mb-1">Quantita</label><input type="number" min="1" value={c.quantita} onChange={(e) => { const arr = [...editForm.colli]; arr[i] = {...arr[i], quantita: parseInt(e.target.value) || 1}; setEditForm({...editForm, colli: arr}); }} className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" /></div>
                  <button type="button" onClick={() => { const arr = editForm.colli.filter((_, j) => j !== i); setEditForm({...editForm, colli: arr.length ? arr : [{tipo:"collo",peso:"",dimensioni:"",quantita:1}]}); }} className="h-9 px-2 text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                </div>
              ))}
              <button type="button" onClick={() => setEditForm({...editForm, colli: [...(editForm.colli||[]), {tipo:"collo",peso:"",dimensioni:"",quantita:1}]})} className="text-xs font-bold text-[#FF5200] uppercase">+ Aggiungi collo</button>
              <ExtraServicesEditor
                value={editForm.extra_services}
                onChange={(es) => setEditForm({ ...editForm, extra_services: es })}
                testIdPrefix="edit-pickup-extras"
              />
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-3">
              <button onClick={() => setEditItem(null)} className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-800">Annulla</button>
              <button onClick={saveEditPickup} className="px-6 py-2 bg-[#FF5200] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#e04a00]" data-testid="save-edit-pickup">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function CostField({ itemId, currentCost, extras, type, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentCost || "");
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const save = async () => {
    try {
      await axios.put(`${API}/admin/${type}/${itemId}/costo`, { costo: parseFloat(value) || 0 }, authHeaders());
      toast.success("Costo aggiornato");
      setEditing(false);
      onUpdate();
    } catch { toast.error("Errore aggiornamento costo"); }
  };

  const extraTot = extrasCost({ extra_services: extras });
  const base = parseFloat(currentCost) || 0;
  const total = base + extraTot;

  return (
    <div className="bg-white p-3 border border-zinc-200 rounded-sm" data-testid={`cost-field-${itemId}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1"><Euro size={10} /> Costo Servizio</p>
      {editing ? (
        <div className="flex items-center gap-2">
          <input type="number" step="0.01" min="0" value={value} onChange={(e) => setValue(e.target.value)}
            className="h-8 px-2 border border-zinc-300 rounded-sm text-sm w-28 focus:border-[#FF5200] outline-none" autoFocus data-testid={`cost-input-${itemId}`} />
          <span className="text-sm text-zinc-500">EUR</span>
          <button onClick={save} className="px-2 py-1 bg-[#FF5200] text-white text-xs font-bold rounded-sm hover:bg-[#e04800]" data-testid={`cost-save-${itemId}`}>Salva</button>
          <button onClick={() => { setEditing(false); setValue(currentCost || ""); }} className="px-2 py-1 text-xs text-zinc-500 hover:text-zinc-700">Annulla</button>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#09090B]" data-testid={`cost-display-${itemId}`}>
              {total > 0 ? `${total.toFixed(2)} EUR` : "Non impostato"}
            </span>
            <button onClick={() => setEditing(true)} className="p-1 text-zinc-400 hover:text-[#FF5200]" title="Modifica costo base" data-testid={`cost-edit-${itemId}`}><PenLine size={12} /></button>
          </div>
          {extraTot > 0 && (
            <p className="text-[10px] text-zinc-500 mt-0.5">Base {base.toFixed(2)} + Extra {extraTot.toFixed(2)}</p>
          )}
        </div>
      )}
    </div>
  );
}
